<template>
  <div
    v-if="props.coach"
    class="i-coach-profile-card"
    :class="{ active: props.activeCoachId === props.coach.id }"
    @click="props.isAssignedCoach && assignedCoachMatchReason ? setCoachAndTab(1) : setCoachAndTab(2)"
  >
    <div class="main">
      <div class="top">
        <div
          v-if="'percentMatch' in props.coach"
          class="badge"
        >
          <div class="inner">Top pick: {{ props.coach.percentMatch }}% Match</div>
        </div>
        <img
          class="photo"
          :src="props.coach.profilePhotoUrl || ''"
        />
        <div class="name">{{ props.coach.fullName }}</div>
      </div>
      <div class="middle">
        <div class="first">
          <div class="rating">
            <img :src="iconStars" />
            {{ props.coach.rating }} stars
          </div>
          <span
            class="review"
            @click.stop="setCoachAndTab(3)"
            >(<u>{{ data.reviews?.length || numberOfReviews }} reviews</u>)</span
          >
        </div>
        <div class="second">
          <div class="stat">
            <div class="image"><img :src="iconClients" /></div>
            <span
              ><strong>{{ props.coach.clientsServed }}</strong> clients served to date</span
            >
          </div>
          <div class="stat">
            <div class="image"><img :src="iconBoost" /></div>
            <span
              >clients see <strong>{{ props.coach.salaryBoostPercent }}%</strong> in avg. salary increase</span
            >
          </div>
        </div>
      </div>
      <div class="bottom">
        <iBaseButton
          v-bind="AVAILABILTY_BUTTON"
          :text="`Check Availability`"
          @click.stop="setCoachAndAvailability"
        />
        <iBaseButton
          v-bind="PROFILE_BUTTON"
          :text="`View Profile`"
          @click.stop="setCoachAndTab(2)"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive } from "vue"
import { azureRadiance600, bluePurpleGradient, white } from "@/styles/colors"

import { GlobalFunctions } from "@/helpers/globalFunctions"
import api from "@/services/apiClient"

import useRandomNumber from "@/composables/useRandomNumber"

import iBaseButton from "@/components/base/BaseButton.vue"

// ASSETS
import iconStars from "@/assets/icons/icon-five-slashed-stars.svg"
import iconBoost from "@/assets/icons/icon-rocket-thick.svg"
import iconClients from "@/assets/icons/icon-user.svg"

// TYPES
import { CoachAssigned, CoachForSelection, CoachReviews } from "@/shared/types/Coach"

// EMITS
const emit = defineEmits(["setActiveCoach", "setActiveTab", "openCalcomModal"])

// PROPS
const props = defineProps({
  coach: {
    type: Object as () => CoachAssigned | CoachForSelection | null,
    required: true
  },
  activeCoachId: {
    type: Number,
    default: null
  },
  isAssignedCoach: {
    type: Boolean,
    default: false
  }
})

// COMPOSABLES
const numberOfReviews = useRandomNumber(props.coach?.fullName || "Full Name", 17, 35)

// DATA
const data = reactive({
  reviews: [] as CoachReviews[]
})

// CONSTANTS
const BUTTON_STYLES = {
  borderColor: azureRadiance600,
  borderRadius: "8px",
  fontWeight: "500",
  width: "100%",
  size: "small"
}
const AVAILABILTY_BUTTON = {
  ...BUTTON_STYLES,
  backgroundImage: bluePurpleGradient,
  backgroundColor: azureRadiance600,
  textColor: white
}
const PROFILE_BUTTON = {
  ...BUTTON_STYLES,
  version: "outline",
  backgroundColor: white,
  textColor: azureRadiance600
}

// COMPUTED
const assignedCoachMatchReason = computed(() => {
  return !props.coach || !("matchReason" in props.coach) ? null : props.coach.matchReason
})

// FUNCTIONS
const setCoachAndTab = (activeTab: number) => {
  emit("setActiveCoach", props.coach?.id)
  emit("setActiveTab", activeTab)

  if (activeTab === 3) GlobalFunctions.scrollInToElement("#selected-coach-details-tabs")
  else GlobalFunctions.scrollInToElement("#selected-coach-details-summary")
}

const setCoachAndAvailability = () => {
  emit("setActiveCoach", props.coach?.id)
  emit("openCalcomModal", true)
  GlobalFunctions.scrollInToElement("#selected-coach-details-summary")
}

const fetchCoachReviews = async () => {
  if (props.coach?.id) {
    try {
      const response = await api.coach.getCoachReviews(props.coach.id, {
        orderBy: "date",
        direction: "desc",
        minRating: 4
      })
      data.reviews = response.reviews
    } catch {
      console.error(`Error fetching coach's reviews. Coach: ${props.coach.id}`)
    }
  }
}

// LIFECYCLE HOOKS
onMounted(async () => await fetchCoachReviews())
</script>

<style lang="scss" scoped>
.i-coach-profile-card {
  @include border-radius-md;

  border: 1px solid $zumthor200;
  background-color: white;
  width: 100%;
  max-width: 445px;
  overflow: hidden;
  cursor: pointer;

  @media screen and (min-width: $breakpoint-md) {
    max-width: 500px;
  }

  @media screen and (min-width: $breakpoint-xl) {
    max-width: 445px;
  }

  @media screen and (min-width: $breakpoint-xxl) {
    max-width: 500px;
  }

  .main {
    @include flex(column, space-between, stretch, $gap: 22px);
    @include border-radius-sm;

    background-color: white;

    .top {
      @include flex(row, start, end, $gap: 18px);

      position: relative;
      padding: 20px 20px 10px;
      background-color: $black50;
      height: 75px;
      border-top-left-radius: 8px;
      border-top-right-radius: 8px;

      @media screen and (min-width: $breakpoint-md) {
        padding: 20px;
        height: 95px;
      }

      .badge {
        @include border-radius-xxl;
        @include text-xs;

        text-transform: uppercase;
        width: fit-content;
        position: absolute;
        top: 8px;
        right: 8px;
        background-image: $blue-purple-gradient;
        font-weight: 600;

        @media screen and (min-width: $breakpoint-md) {
          font-weight: 700;
        }

        .inner {
          @include border-radius-xxl;

          padding: 4px 8px;
          background-color: #ffffffda;
          mix-blend-mode: screen;
        }
      }

      .name {
        @include text-md;

        font-weight: 600;

        @media screen and (min-width: $breakpoint-md) {
          @include text-lg;
        }
      }

      .photo {
        @include border-radius-rounded;

        width: 80px;
        height: 80px;
        transform: translate(0, 40%);

        @media screen and (min-width: $breakpoint-md) {
          width: 105px;
          height: 105px;
          transform: translate(0, 50%);
        }
      }
    }

    .middle {
      @include flex(row, start, center, $gap: 18px);

      padding: 0 20px;

      @media screen and (min-width: $breakpoint-md) {
        padding: 20px 20px 0;
      }

      @media screen and (min-width: $breakpoint-xl) {
        padding: 20px;
      }

      .first {
        @include text-xs;

        font-weight: 500;
        color: $bilbao500;
        width: 105px;
        text-align: center;

        @media screen and (min-width: $breakpoint-md) {
          @include text-sm;
        }

        .rating {
          @include flex(column, start, stretch, $gap: 4px);

          img {
            vertical-align: text-top;
            height: 13px;
          }
        }

        .review {
          text-wrap-mode: nowrap;

          &:hover {
            color: $bilbao700;
          }
        }
      }

      .second {
        @include flex(column, center, start, $gap: 5px);
        @include text-sm;

        @media screen and (min-width: $breakpoint-md) {
          @include text-md;
        }

        .stat {
          @include flex(row, start, baseline, $gap: 6px);

          .image {
            width: 20px;
            flex: 0 0 20px;
          }
        }
      }
    }

    .bottom {
      @include flex(column, start, center, $gap: 15px);

      padding: 0 20px 20px;
      width: 100%;

      @media screen and (min-width: $breakpoint-xl) {
        display: none;
      }
    }
  }

  &.active {
    background-image: $blue-purple-gradient;
    padding: 4px;

    .main {
      .top {
        padding: 16px 16px 10px;
        height: 71px;

        @media screen and (min-width: $breakpoint-md) {
          padding: 16px 16px 20px;
          height: 91px;
        }
      }

      .middle {
        padding: 0 16px;

        @media screen and (min-width: $breakpoint-md) {
          padding: 20px 16px 0;
        }

        @media screen and (min-width: $breakpoint-xl) {
          padding: 20px 16px 16px;
        }
      }

      .bottom {
        padding: 0 16px 16px;
      }
    }
  }
}
</style>
